import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sipariş kartı render edildiğinde, eğer sipariş kağıdı fotoğrafı varsa
 * otomatik yüklenip görüntülenir. Kullanıcının "Aç" düğmesine basmasına
 * gerek yok. Görüntüsüz siparişlerde sade bir placeholder gösterilir.
 *
 * NOT: List response payload'unun küçük kalması için (Cloudflare/iOS Safari)
 * fotoğraflar burada /api/orders/{id}/image endpoint'inden lazy fetch edilir.
 *
 * Props:
 *  - orderId
 *  - hasImage
 *  - alt
 *  - onPreview(src)
 *  - testIdPrefix
 *  - size: "lg" | "md"
 */
export function OrderPaperPhoto({ orderId, hasImage, alt, onPreview, testIdPrefix, size = "lg" }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  const widths  = size === "lg" ? "w-full sm:w-56 md:w-64" : "w-full sm:w-44";
  const heights = size === "lg" ? "max-h-[65vh] sm:max-h-none" : "max-h-[55vh] sm:max-h-none";

  // Sipariş kartı mount olduğu anda otomatik yükle.
  useEffect(() => {
    if (!hasImage || !orderId) return;
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/image`);
        if (!cancelled) setSrc(data.image_url);
      } catch (e) {
        if (!cancelled) {
          setErrored(true);
          // Toast atmıyoruz — birden fazla sipariş yüklenirken kullanıcıyı
          // bombalamamak için sessiz hata; "Tekrar Dene" tuşu sunuyoruz.
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, hasImage]);

  const retry = async () => {
    setErrored(false);
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}/image`);
      setSrc(data.image_url);
    } catch (e) {
      setErrored(true);
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // 1) Fotoğraf yoksa — sade placeholder
  if (!hasImage) {
    return (
      <div
        data-testid={`${testIdPrefix}-no-photo`}
        className={`${widths} shrink-0 self-stretch min-h-[10rem] sm:min-h-[14rem] flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-200 bg-zinc-50 text-zinc-400`}
      >
        <FileText size={28} strokeWidth={1.5} />
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-center px-2">
          Sipariş Kağıdı Yok
        </div>
      </div>
    );
  }

  // 2) Yükleniyor
  if (loading) {
    return (
      <div
        data-testid={`${testIdPrefix}-loading`}
        className={`${widths} shrink-0 self-stretch min-h-[10rem] sm:min-h-[14rem] flex flex-col items-center justify-center gap-2 border-2 border-zinc-200 bg-zinc-50 text-zinc-500`}
      >
        <Loader2 size={28} className="animate-spin" />
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]">Yükleniyor…</div>
      </div>
    );
  }

  // 3) Hata olmuş, tekrar dene butonu
  if (errored && !src) {
    return (
      <button
        type="button"
        onClick={retry}
        data-testid={`${testIdPrefix}-retry`}
        className={`${widths} shrink-0 self-stretch min-h-[10rem] sm:min-h-[14rem] flex flex-col items-center justify-center gap-2 border-2 border-red-300 bg-red-50 text-red-700 hover:border-red-500 transition-colors`}
      >
        <FileText size={28} strokeWidth={1.5} />
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-center px-2">
          Yüklenemedi — Tekrar Dene
        </div>
      </button>
    );
  }

  // 4) Resim hazır — gösteriyoruz
  return (
    <button
      type="button"
      onClick={() => src && onPreview?.(src)}
      data-testid={`${testIdPrefix}-thumb`}
      className={`${widths} shrink-0 self-stretch overflow-hidden border-2 border-zinc-200 bg-zinc-100 flex items-center justify-center group`}
    >
      <img
        src={src}
        alt={alt}
        className={`w-full h-full ${heights} object-contain group-hover:scale-[1.01] transition-transform`}
      />
    </button>
  );
}
